import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar, formatIDR } from "@/components/AppTopBar";
import { RupiahInput } from "@/components/RupiahInput";

export default function SupplierDeposits() {
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [summary, setSummary] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [depositForm, setDepositForm] = useState({ account_id: "", amount: "", reference: "" });
  const [allocationForm, setAllocationForm] = useState({ purchase_id: "", amount: "", reference: "" });
  const [refundForm, setRefundForm] = useState({ account_id: "", amount: "", reference: "" });

  const activeSuppliers = useMemo(() => suppliers.filter((s) => s.status === "ACTIVE"), [suppliers]);
  const supplierPurchases = useMemo(
    () => purchases.filter((p) => p.supplier_id === supplierId && p.status !== "DRAFT"),
    [purchases, supplierId],
  );

  const loadBase = async () => {
    try {
      const [s, a, p] = await Promise.all([
        api.get("/suppliers"),
        api.get("/accounts"),
        api.get("/purchases"),
      ]);
      setSuppliers(s.data);
      setAccounts(a.data);
      setPurchases(p.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const loadSupplier = async (id) => {
    if (!id) {
      setSummary(null); setDeposits([]); setAllocations([]); setRefunds([]); return;
    }
    try {
      const [sum, deps, allocs, refs] = await Promise.all([
        api.get(`/supplier-deposits/summary/${id}`),
        api.get(`/supplier-deposits?supplier_id=${id}`),
        api.get(`/supplier-deposits/allocations?supplier_id=${id}`),
        api.get(`/supplier-deposits/refunds?supplier_id=${id}`),
      ]);
      setSummary(sum.data);
      setDeposits(deps.data);
      setAllocations(allocs.data);
      setRefunds(refs.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => { loadBase(); }, []);
  useEffect(() => { loadSupplier(supplierId); }, [supplierId]);

  const createDeposit = async () => {
    setError(""); setNotice("");
    const amount = Number(depositForm.amount || 0);
    if (!supplierId) return setError("Pilih Supplier terlebih dahulu.");
    if (!depositForm.account_id) return setError("Pilih rekening BANK.");
    if (amount <= 0) return setError("Nominal Deposit harus lebih dari 0.");
    try {
      await api.post("/supplier-deposits", {
        supplier_id: supplierId,
        account_id: depositForm.account_id,
        amount,
        reference: depositForm.reference || null,
        idempotency_key: `SUPDEP-${supplierId.slice(0, 8)}-${Date.now()}`,
      });
      setDepositForm({ account_id: "", amount: "", reference: "" });
      setNotice("Supplier Deposit berhasil dicatat. Dana keluar dari BANK sebagai MODAL.");
      await Promise.all([loadBase(), loadSupplier(supplierId)]);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const createAllocation = async () => {
    setError(""); setNotice("");
    const amount = Number(allocationForm.amount || 0);
    if (!supplierId) return setError("Pilih Supplier terlebih dahulu.");
    if (!allocationForm.purchase_id) return setError("Pilih Purchase.");
    if (amount <= 0) return setError("Nominal Allocation harus lebih dari 0.");
    try {
      await api.post("/supplier-deposits/allocations", {
        supplier_id: supplierId,
        purchase_id: allocationForm.purchase_id,
        amount,
        reference: allocationForm.reference || null,
        idempotency_key: `SUPALLOC-${allocationForm.purchase_id.slice(0, 8)}-${Date.now()}`,
      });
      setAllocationForm({ purchase_id: "", amount: "", reference: "" });
      setNotice("Deposit berhasil dialokasikan ke Purchase tanpa cash-out kedua.");
      await loadSupplier(supplierId);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const createRefund = async () => {
    setError(""); setNotice("");
    const amount = Number(refundForm.amount || 0);
    if (!supplierId) return setError("Pilih Supplier terlebih dahulu.");
    if (!refundForm.account_id) return setError("Pilih rekening BANK tujuan refund.");
    if (amount <= 0) return setError("Nominal Refund harus lebih dari 0.");
    if (summary && amount > Number(summary.available_deposit || 0)) return setError("Nominal Refund melebihi saldo Deposit tersedia.");
    try {
      await api.post("/supplier-deposits/refunds", {
        supplier_id: supplierId,
        account_id: refundForm.account_id,
        amount,
        reference: refundForm.reference || null,
        idempotency_key: `SUPREF-${supplierId.slice(0, 8)}-${Date.now()}`,
      });
      setRefundForm({ account_id: "", amount: "", reference: "" });
      setNotice("Refund Deposit tercatat. Dana kembali ke BANK sebagai MODAL.");
      await Promise.all([loadBase(), loadSupplier(supplierId)]);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const reverseAllocation = async (allocationId) => {
    const reason = window.prompt("Alasan membatalkan Allocation Deposit ini:");
    if (!reason || !reason.trim()) return;
    setError(""); setNotice("");
    try {
      await api.post(`/supplier-deposits/allocations/${allocationId}/reverse`, { reason: reason.trim() });
      setNotice("Allocation dibatalkan. Saldo Deposit dan outstanding Purchase dipulihkan tanpa cash movement.");
      await loadSupplier(supplierId);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const reverseRefund = async (refundId) => {
    const reason = window.prompt("Alasan membatalkan Refund Deposit ini:");
    if (!reason || !reason.trim()) return;
    setError(""); setNotice("");
    try {
      await api.post(`/supplier-deposits/refunds/${refundId}/reverse`, { reason: reason.trim() });
      setNotice("Refund dibatalkan. BANK OUT dan identitas MODAL dibalik secara canonical.");
      await Promise.all([loadBase(), loadSupplier(supplierId)]);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (user && user.role !== "OWNER") {
    return <div className="app-shell"><AppTopBar active="supplier-deposits" /><main className="content"><div className="alert-error">Akses ditolak.</div></main></div>;
  }

  return (
    <div className="app-shell">
      <AppTopBar active="supplier-deposits" />
      <main className="content">
        {notice && <div className="alert-ok">{notice}</div>}
        {error && <div className="alert-error">{error}</div>}

        <section className="panel">
          <div className="panel-head"><h2 className="panel-title">Supplier Deposit</h2></div>
          <div className="form-grid">
            <div>
              <label className="field-label">Supplier</label>
              <select className="field-input" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                <option value="">-- pilih Supplier --</option>
                {activeSuppliers.map((s) => <option key={s.id} value={s.id}>{s.supplier_code} — {s.name}</option>)}
              </select>
            </div>
          </div>
        </section>

        {summary && (
          <section className="panel">
            <h2 className="panel-title">Saldo Deposit — {summary.supplier_name}</h2>
            <div className="meta-grid">
              <div><span className="meta-label">Total Deposit</span><span className="mono">{formatIDR(summary.total_deposited)}</span></div>
              <div><span className="meta-label">Terpakai ke Purchase</span><span className="mono">{formatIDR(summary.allocated)}</span></div>
              <div><span className="meta-label">Refund Aktif</span><span className="mono">{formatIDR(summary.refunded)}</span></div>
              <div><span className="meta-label">Saldo Tersedia</span><span className="mono">{formatIDR(summary.available_deposit)}</span></div>
            </div>
          </section>
        )}

        {supplierId && (
          <>
            <section className="panel">
              <h2 className="panel-title">Tambah Deposit</h2>
              <p className="hint">Cash-out hanya dari rekening BANK dan menggunakan dana MODAL. Deposit belum otomatis membayar Purchase.</p>
              <div className="form-grid">
                <div><label className="field-label">Rekening BANK</label><select className="field-input" value={depositForm.account_id} onChange={(e) => setDepositForm({ ...depositForm, account_id: e.target.value })}><option value="">-- pilih BANK --</option>{accounts.filter((a) => a.is_active && a.account_type === "BANK").map((a) => <option key={a.account_id} value={a.account_id}>{a.account_name} — {formatIDR(a.current_balance)} | MODAL {formatIDR(a.modal_available)}</option>)}</select></div>
                <div><label className="field-label">Nominal</label><RupiahInput className="field-input" value={depositForm.amount} onChange={(v) => setDepositForm({ ...depositForm, amount: v })} /></div>
                <div><label className="field-label">Referensi</label><input className="field-input" value={depositForm.reference} onChange={(e) => setDepositForm({ ...depositForm, reference: e.target.value })} /></div>
              </div>
              <div className="form-actions"><button className="btn-primary" onClick={createDeposit}>Catat Deposit</button></div>
            </section>

            <section className="panel">
              <h2 className="panel-title">Pakai Deposit untuk Purchase</h2>
              <p className="hint">Allocation hanya mengurangi saldo Deposit dan outstanding Purchase. Tidak ada BCA OUT kedua.</p>
              <div className="form-grid">
                <div><label className="field-label">Purchase</label><select className="field-input" value={allocationForm.purchase_id} onChange={(e) => setAllocationForm({ ...allocationForm, purchase_id: e.target.value })}><option value="">-- pilih Purchase --</option>{supplierPurchases.map((p) => <option key={p.id} value={p.id}>{p.purchase_no} — {formatIDR(p.purchase_total)}</option>)}</select></div>
                <div><label className="field-label">Nominal Allocation</label><RupiahInput className="field-input" value={allocationForm.amount} onChange={(v) => setAllocationForm({ ...allocationForm, amount: v })} /></div>
                <div><label className="field-label">Referensi</label><input className="field-input" value={allocationForm.reference} onChange={(e) => setAllocationForm({ ...allocationForm, reference: e.target.value })} /></div>
              </div>
              <div className="form-actions"><button className="btn-primary" onClick={createAllocation}>Gunakan Deposit</button></div>
            </section>

            <section className="panel">
              <h2 className="panel-title">Refund Saldo Deposit</h2>
              <p className="hint">Gunakan saat Supplier mengembalikan saldo Deposit yang belum terpakai. Dana masuk BANK dan kembali sebagai MODAL.</p>
              <div className="form-grid">
                <div><label className="field-label">Rekening BANK Tujuan</label><select className="field-input" value={refundForm.account_id} onChange={(e) => setRefundForm({ ...refundForm, account_id: e.target.value })}><option value="">-- pilih BANK --</option>{accounts.filter((a) => a.is_active && a.account_type === "BANK").map((a) => <option key={a.account_id} value={a.account_id}>{a.account_name} — {formatIDR(a.current_balance)}</option>)}</select></div>
                <div><label className="field-label">Nominal Refund</label><RupiahInput className="field-input" value={refundForm.amount} onChange={(v) => setRefundForm({ ...refundForm, amount: v })} /></div>
                <div><label className="field-label">Referensi</label><input className="field-input" value={refundForm.reference} onChange={(e) => setRefundForm({ ...refundForm, reference: e.target.value })} /></div>
              </div>
              <div className="form-actions"><button className="btn-primary" onClick={createRefund}>Catat Refund</button></div>
            </section>

            <section className="panel">
              <h2 className="panel-title">Riwayat Deposit</h2>
              <div className="table-scroll"><table className="data-table"><thead><tr><th>Tanggal</th><th>Rekening</th><th>Nominal</th><th>Referensi</th><th>Status</th></tr></thead><tbody>
                {deposits.length === 0 && <tr><td colSpan={5} className="empty-cell">Belum ada Deposit.</td></tr>}
                {deposits.map((d) => <tr key={d.id}><td>{d.deposit_date}</td><td>{d.account_name || "-"}</td><td className="mono">{formatIDR(d.amount)}</td><td>{d.reference || "-"}</td><td>{d.status}</td></tr>)}
              </tbody></table></div>
            </section>

            <section className="panel">
              <h2 className="panel-title">Riwayat Allocation</h2>
              <div className="table-scroll"><table className="data-table"><thead><tr><th>Tanggal</th><th>Purchase</th><th>Nominal</th><th>Referensi</th><th>Status</th><th>Aksi</th></tr></thead><tbody>
                {allocations.length === 0 && <tr><td colSpan={6} className="empty-cell">Belum ada Allocation.</td></tr>}
                {allocations.map((a) => { const p = purchases.find((x) => x.id === a.purchase_id); return <tr key={a.id}><td>{a.allocation_date}</td><td>{p?.purchase_no || a.purchase_id}</td><td className="mono">{formatIDR(a.amount)}</td><td>{a.reference || "-"}</td><td>{a.status}</td><td>{a.status === "POSTED" ? <button className="btn-mini" onClick={() => reverseAllocation(a.id)}>Reverse</button> : "-"}</td></tr>; })}
              </tbody></table></div>
            </section>

            <section className="panel">
              <h2 className="panel-title">Riwayat Refund Deposit</h2>
              <div className="table-scroll"><table className="data-table"><thead><tr><th>Tanggal</th><th>Rekening</th><th>Nominal</th><th>Referensi</th><th>Status</th><th>Aksi</th></tr></thead><tbody>
                {refunds.length === 0 && <tr><td colSpan={6} className="empty-cell">Belum ada Refund.</td></tr>}
                {refunds.map((r) => <tr key={r.id}><td>{r.refund_date}</td><td>{r.account_name || "-"}</td><td className="mono">{formatIDR(r.amount)}</td><td>{r.reference || "-"}</td><td>{r.status}</td><td>{r.status === "POSTED" ? <button className="btn-mini" onClick={() => reverseRefund(r.id)}>Reverse</button> : "-"}</td></tr>)}
              </tbody></table></div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
